<nz-card class="card">
  <h5>Issue Overview:</h5>
  <p *ngIf="enclosureBk">
    <b
      >{{ storageLevel.String | levelIdentifierToLevelName | titlecase }} Location:
      {{ enclosureBk | componentNameTranslation }} &rarr; {{ componentBk | componentNameTranslation }}</b
    >
  </p>
  <p>
    <b>{{ componentBk | componentNameTranslation }} is currently non-operational</b> and is not contributing to usable energy.
    This issue can reduce {{ storageLevel.System | levelIdentifierToLevelName }} availability and revenue generation. Possible
    causes include:
  </p>
  <ul class="list-disc">
    <li>Electrical disconnection (e.g., fuse failure, cable issue).</li>
    <li>
      {{ storageLevel.Cell | levelIdentifierToLevelName | titlecase }} or
      {{ storageLevel.Module | levelIdentifierToLevelName }}-level faults, such as weak
      {{ storageLevel.Cell | levelIdentifierToLevelName }} failure or extreme imbalance.
    </li>
    <li>Safety lockouts or protection mechanisms triggered by BMS.</li>
    <li>Manual shutdown for scheduled maintenance or unexpected downtime.</li>
  </ul>
  <br />
  <p>
    This situation requires attention because persistent {{ storageLevel.String | levelIdentifierToLevelName }}-level failures can
    indicate systemic issues and impact warranty or performance guarantees.
  </p>
</nz-card>
<nz-card class="card">
  <h5>Why This Matters for You:</h5>
  <ul class="list-disc">
    <li>
      <b>Maximize the usable energy capacity and power</b> by ensuring all {{ storageLevel.String | levelIdentifierToLevelName }}s
      are operational directly increases your revenue potential.
    </li>
    <li>
      <b>Gain transparency in the component availability</b> and strengthen your position in contractual and technical discussions
      with 3rd parties.
    </li>
  </ul>
  <p>
    Individual component performance can have significant impact on the overall
    {{ storageLevel.System | levelIdentifierToLevelName }} performance, identify issues early to avoid larger, negative impacts.
  </p>
</nz-card>
<nz-card class="card">
  <h5>Recommendations:</h5>
  <p>To restore functionality and assess operational risk, take the possible following steps:</p>
  <ol class="list-decimal">
    <li><b>Check system logs</b> for fault codes, disconnection events, or unusual triggers.</li>
    <li>
      <b>Inspect electrical components:</b> Validate fuses, cables, and
      {{ storageLevel.Module | levelIdentifierToLevelName }} connectivity at the affected
      {{ storageLevel.String | levelIdentifierToLevelName }}.
    </li>
    <li>
      <b>Review {{ storageLevel.String | levelIdentifierToLevelName }}-level balancing trends</b> via Smart Alerts using
      {{ storageLevel.String | levelIdentifierToLevelName | titlecase }}
      {{ storageLevel.Cell | levelIdentifierToLevelName | titlecase }} Voltage Spreads or
      {{ storageLevel.String | levelIdentifierToLevelName | titlecase }}
      {{ storageLevel.Module | levelIdentifierToLevelName | titlecase }}
      Temperature Spreads alerts and Performance State of Charge and Temperature to detect whether the issue arises from SoC,
      voltage or temperature imbalance.
    </li>
    <li>
      <b>Consult your O&M provider</b> to plan an on-site inspection, especially if the fault persists beyond automatic retries or
      recovery routines.
    </li>
    <li>
      <b>Use manufacturer or integrator documentation</b> to cross-reference known failure modes and recommended corrective
      actions.
    </li>
  </ol>
</nz-card>
<nz-card class="card">
  <h5>Documenting the issue:</h5>
  <p>To facilitate a timely and informed resolution, take the following actions:</p>
  <ul class="list-disc">
    <li>
      <b>Export data (CSV)</b> from below. This enables easy sharing of relevant data points with other internal or external
      stakeholders.
    </li>
    <li>
      <b>Use the alert timestamp</b> in Smart Alerts to pinpoint when the issue started and whether it’s intermittent or
      ongoing.
    </li>
    <li>
      <b>Open the Data Explorer</b> from the overview of affected components row item in Smart Alerts, select
      {{ storageLevel.String | levelIdentifierToLevelName | titlecase }} Current or
      {{ storageLevel.String | levelIdentifierToLevelName | titlecase }} Equivalent Full Cycles to visualize the fault history and
      any trends.
    </li>
    <li>
      <b>Compile a summary report:</b> Include the exported CSV, relevant screenshots, and a short description on what has been
      observed so far. This will support both internal reviews and technical escalations if needed.
    </li>
  </ul>
</nz-card>
<nz-card class="card">
  <h5>Guidance for Collaboration with Service and Hardware Providers:</h5>
  <p>To support productive discussions with your system integrator or OEM:</p>
  <ul class="list-disc">
    <li>
      Share the <b>exported CSV</b> and <b>Data Explorer screenshots</b> that clearly show the timeline, operating conditions, and
      affected metrics.
    </li>
    <li>Include insights from your summary report, focus on the <b>impact on performance</b> and <b>steps already taken</b>.</li>
    <li>
      Reference <b>recurring imbalance trends or repeated fault codes</b> that may indicate underlying design or component issues.
    </li>
    <li>
      <b>Request clarification or input</b> from the supplier based on their known failure modes or product support documentation.
    </li>
  </ul>
</nz-card>
